/* eslint-disable no-unused-vars */
import { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { concat } from 'lodash'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import PostCard from '@/components/base/PostCard'
import Section from '@/components/base/Section'
import Spinner from '@/components/base/Spinner'
import Typography from '@/components/base/Typography'
import { useStore } from '@/contexts/store'
import { normalizePostsCollection } from '@/utils/pageData'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import { cn } from '@/lib/utils'
import { WPHeaders } from '@/types'

export type PostArchiveProps = HTMLAttributes<HTMLElement> &
  Partial<{
    post_type: string
    taxonomy: string
    taxonomy_term: string
    use_pagination: boolean
    per_page: string
    headers: WPHeaders
    collection: any[]
  }>

const PostArchive = ({
  post_type,
  collection: posts,
  taxonomy,
  taxonomy_term,
  headers: postHeaders,
  use_pagination,
  per_page,
  className,
  ...props
}: PostArchiveProps) => {
  const [headers, setHeaders] = useState<WPHeaders>(postHeaders!)
  const [collection, setCollection] = useState<any[]>(posts as any[])
  const [pagination, setPagination] = useState(-1)
  const [fetching, setFetching] = useState(false)
  const [needsPagination, setNeedsPagination] = useState(
    headers['X-WP-TotalPages'] > 1
  )
  const { strings } = useStore()

  const getParams = useCallback(
    (page: number) => {
      const perPage = use_pagination ? per_page : 1000
      const params = {
        per_page: perPage,
        page
      } as any

      return {
        params
      }
    },
    [use_pagination, per_page]
  )

  const getPosts = useCallback(
    async (page: number) => {
      const postType = 'posts'

      setFetching(true)

      const { data, headers } = await getPromiseFromFetch({
        method: 'get',
        url: `${API.BASE}/${postType}`,
        params: getParams(page)
      })

      setHeaders({
        'X-WP-Total': parseInt(headers['x-wp-total']),
        'X-WP-TotalPages': parseInt(headers['x-wp-totalpages'])
      })

      setCollection((previousCollection) =>
        concat(previousCollection, normalizePostsCollection(data, strings))
      )

      setNeedsPagination(page < parseInt(headers['x-wp-totalpages']))

      setFetching(false)
    },
    [strings, getParams]
  )

  useEffect(() => {
    if (pagination < 0) return
    getPosts(pagination)
  }, [pagination, getPosts])

  return (
    <Section className={cn([className])} {...props}>
      <Container>
        <Grid className="gap-y-gutter">
          {collection?.map((post, index) => (
            <div
              key={`post-card-${index}`}
              className="col-span-4 md:col-span-3 lg:col-span-4"
            >
              <PostCard {...post} />
            </div>
          ))}

          {use_pagination && needsPagination ? (
            <div
              className={cn([
                'col-span-4 md:col-span-6 lg:col-span-12',
                'mt-[3rem] flex justify-center'
              ])}
            >
              <span className="relative inline-flex">
                <Button
                  variant="default"
                  className="relative"
                  disabled={fetching}
                  onClick={() => {
                    setPagination((pagination) =>
                      pagination < 0 ? 2 : pagination + 1
                    )
                  }}
                >
                  <span data-label={strings?.labels.load_more}>
                    <Typography
                      variant="label"
                      className={cn([{ invisible: fetching }])}
                    >
                      {strings?.labels.load_more}
                    </Typography>
                  </span>
                </Button>

                {fetching ? (
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <Spinner
                      variant="small"
                      className="[&>svg]:stroke-background"
                    />
                  </span>
                ) : null}
              </span>
            </div>
          ) : null}
        </Grid>
      </Container>
    </Section>
  )
}

export default PostArchive
